import { useEventDesignLayoutTypeQuery } from './useEventDesignLayoutType.graphql.generated';
import { EventDesignPurpose } from '@graphql/generated-base';
import type { DesignLayoutType } from '@graphql/generated-base';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { shouldUseEventDesignDraft } from '@shared/utils/eventDesignPurpose';

// Captured once at module load: preview navigation can push/pop history entries,
// which would strip query params mid-session and flip purpose unexpectedly.
const isPreviewDraftContext = shouldUseEventDesignDraft();

type UseEventDesignLayoutTypeReturn = Readonly<{
  layoutType: DesignLayoutType | null;
  loading: boolean;
}>;

export const useEventDesignLayoutType = ({ skip = false }: Readonly<{ skip?: boolean }> = {}): UseEventDesignLayoutTypeReturn => {
  const { eventHandle } = useEventInfo();
  const [isWebsitePublishCapabilityEnabled, isWebsitePublishCapabilityLoading] = useIsFeatureEnabled({ eventFeatureFlagKey: 'useWebsitePublishedState' });

  const shouldUseDraft = isPreviewDraftContext && !isWebsitePublishCapabilityEnabled;
  const purpose = shouldUseDraft ? EventDesignPurpose.draft : EventDesignPurpose.live;

  const { data, loading } = useEventDesignLayoutTypeQuery({
    batchMode: 'fast',
    variables: { eventHandle: eventHandle || '', eventDesignPurpose: purpose },
    skip: skip || !eventHandle || (isPreviewDraftContext && isWebsitePublishCapabilityLoading),
    fetchPolicy: 'cache-first',
  });

  return {
    layoutType: data?.eventByName?.eventDesign?.websiteLayout?.layoutType ?? null,
    loading: skip ? false : loading,
  };
};
